import React, { useEffect } from 'react';
import Styles from '../../assets/css/components/logistic/logisticView.module.scss';
import { getLogisticById, getCountries, getStates, getCities } from '../../services/Api';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../utils/Loader';

const LogisticView = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [logisticData, setLogisticData] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [countryName, setCountryName] = React.useState('N/A');
    const [stateName, setStateName] = React.useState('N/A');
    const [cityName, setCityName] = React.useState('N/A');

    // Function to get country name by ID
    const getCountryName = async (countryId) => {
        try {
            const response = await getCountries();
            const apiData = response.data || response;
            if (apiData && apiData.data) {
                const country = apiData.data.find(item => item.id === countryId);
                return country ? country.name : 'N/A';
            }
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
        return 'N/A';
    };

    // Function to get state name by ID
    const getStateName = async (stateId) => {
        try {
            const response = await getStates();
            const apiData = response.data || response;
            if (apiData && apiData.data) {
                const state = apiData.data.find(item => item.id === stateId);
                return state ? state.name : 'N/A';
            }
        } catch (error) {
            console.error("Error fetching states:", error);
        }
        return 'N/A';
    };

    // Function to get city name by ID
    const getCityName = async (cityId) => {
        try {
            const response = await getCities();
            const apiData = response.data || response;
            if (apiData && apiData.data) {
                const city = apiData.data.find(item => item.id === cityId);
                return city ? city.name : 'N/A';
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
        }
        return 'N/A';
    };

    // Function to format date to DD/MM/YYYY
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        const fetchLogisticData = async () => {
            setIsLoading(true);
            try {
                const response = await getLogisticById(id);
                
                // Check if it's an Axios response (has response.data) or direct data
                const apiData = response.data || response;
                
                if (apiData && apiData.status === 200) {
                    const logisticData = apiData.data;
                    setLogisticData(logisticData);

                    // Fetch country, state, and city names if warehouse exists
                    if (logisticData.warehouse) {
                        const warehouse = logisticData.warehouse;
                        
                        // Fetch country name
                        if (warehouse.country_id) {
                            const countryName = await getCountryName(warehouse.country_id);
                            setCountryName(countryName);
                        }

                        // Fetch state name
                        if (warehouse.state_id) {
                            const stateName = await getStateName(warehouse.state_id);
                            setStateName(stateName);
                        }

                        // Fetch city name
                        if (warehouse.city_id) {
                            const cityName = await getCityName(warehouse.city_id);
                            setCityName(cityName);
                        }
                    }
                } else {
                    console.error("Logistic not found or invalid response format:", apiData);
                    alert("Logistic not found. Please check the logistic ID.");
                    navigate("/company");
                }
            } catch (error) {
                console.error("Error fetching logistic details:", error);
                alert("Error fetching logistic details. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchLogisticData();
        } else {
            alert("Invalid logistic ID.");
            navigate("/company");
        }
    }, [id, navigate]);

    if (isLoading) {
        return (
            <div className={Styles.logistic_view}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '60vh',
                    width: '100%'
                }}>
                    <Loader />
                </div>
            </div>
        );
    }

    if (!logisticData) {
        return (
            <div className={Styles.logistic_view}>
                <div className={Styles.error_message}>
                    <h3>Logistic data not found</h3>
                    <button onClick={() => navigate("/logistic")}>
                        Go Back to Logistics
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={Styles.logistic_view}>
            <div className={Styles.logistic_heading}>
                <h2>Logistic View</h2>
                <button 
                    className={Styles.back_btn} 
                    onClick={() => navigate("/logistic")}
                >
                    Back
                </button>
            </div>

            <div className={Styles.logistic_content}>
                {/* Logistic Card */}
                <div className={Styles.logistic_card}>
                    <div className={Styles.card_header}>
                        <h3>Logistic Details</h3>
                        <span className={`${Styles.status_badge} ${logisticData.status === 1 ? Styles.active : Styles.inactive}`}>
                            {logisticData.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    
                    <div className={Styles.logistic_details}>
                        <label>Logistic Name</label>
                        <p>{logisticData.name || 'N/A'}</p>
                    </div>
                    
                    <div className={Styles.logistic_details}>
                        <label>Email</label>
                        <p>{logisticData.email || 'N/A'}</p>
                    </div>
                    
                    <div className={Styles.logistic_details}>
                        <label>Phone Number</label>
                        <p>{logisticData.phone_number || 'N/A'}</p>
                    </div>
                    
                    <div className={Styles.logistic_details}>
                        <label>Created Date</label>
                        <p>{formatDate(logisticData.created_at)}</p>
                    </div>
                </div>

                {/* Warehouse Card */}
                <div className={Styles.warehouse_card}>
                    <div className={Styles.card_header}>
                        <h3>Warehouse Details</h3>
                        <span className={`${Styles.status_badge} ${logisticData.warehouse && logisticData.warehouse.status === 1 ? Styles.active : Styles.inactive}`}>
                            {logisticData.warehouse && logisticData.warehouse.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    
                    <div className={Styles.warehouse_details}>
                        <label>Warehouse Name</label>
                        <p>{logisticData.warehouse ? logisticData.warehouse.name : 'N/A'}</p>
                    </div>
                    
                    <div className={Styles.warehouse_details}>
                        <label>Warehouse Address</label>
                        <p>{logisticData.warehouse ? logisticData.warehouse.address : 'N/A'}</p>
                    </div>
                    
                    <div className={Styles.warehouse_details}>
                        <label>Country</label>
                        <p>{countryName}</p>
                    </div>
                    
                    <div className={Styles.warehouse_details}>
                        <label>State</label>
                        <p>{stateName}</p>
                    </div>
                    
                    <div className={Styles.warehouse_details}>
                        <label>City</label>
                        <p>{cityName}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogisticView;